import {Button, DropdownItem, DropdownItemProps, openDropdown} from 'platform/components';
import {Box, HStack, Heading, Icon, Show, Text, VStack} from 'platform/foundation';
import {Lightbox, useLightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotEmpty} from 'ramda';

import i18n from '@dms/i18n';

import {DOT_CHARACTER, TestIdProps, buildArray, parseDate, suffixTestId} from 'shared/web';

import {
  prepareVehicleAlbumImageUrl,
  VEHICLE_ALBUM_IMAGE_SIZE,
} from '../../VehicleAlbumGallery/utils';
import {VehicleAlbumImage} from '../../VehicleAlbumGallery/VehicleAlbumImage';
import {VehicleAlbumImageGrid} from '../../VehicleAlbumGallery/VehicleAlbumImageGrid';
import {getPrecedingPlayableVideos} from '../../VideoPreview/utils/getPrecedingPlayableVideos';
import {isVideoPlayable} from '../../VideoPreview/utils/isVideoPlayable';
import {VideoPreview} from '../../VideoPreview/VideoPreview';
import {useServiceCaseLightboxAssets} from '../hooks/useServiceCaseLightboxAssets';
import {ServiceCasePhotoAlbum} from '../types/ServiceCasePhotoAlbum';
import {ServiceCasePhotoTile} from '../types/ServiceCasePhotoTile';

type ServiceCasePhotoMenuItem = DropdownItemProps & {label: string};

interface ServiceCasePhotosAlbumProps extends TestIdProps {
  serviceCaseId: string;
  album: ServiceCasePhotoAlbum;
  canDeletePhotos: boolean;
  processingPhotoIds: string[];
  onSetCoverPhoto: (photo: ServiceCasePhotoTile) => void;
  onUnsetCoverPhoto: (photo: ServiceCasePhotoTile) => void;
  onDeletePhoto: (photo: ServiceCasePhotoTile) => void;
  onDownload: (albumId: string) => void;
}

export function ServiceCasePhotosAlbum(props: ServiceCasePhotosAlbumProps) {
  const formatDateTime = useDateTimeFormatter();
  const {makeLightboxPhotos, makeLightboxVideos, photoDownloadSizeDialog} =
    useServiceCaseLightboxAssets();

  const [lightboxControls, {onOpen}] = useLightbox(
    `serviceCasePhotos-${props.serviceCaseId}-${props.album.id}`
  );

  const buildPhotoContextMenu = (photo: ServiceCasePhotoTile) => {
    const isProcessing = props.processingPhotoIds.includes(photo.id);

    return buildArray<ServiceCasePhotoMenuItem>([])
      .whenNot(photo.isCoverPhoto, () => ({
        label: i18n.t('general.actions.setAsCoverPhoto'),
        prefix: <Icon value="image/photo" />,
        isDisabled: isProcessing,
        onClick: () => props.onSetCoverPhoto(photo),
      }))
      .when(photo.isCoverPhoto, () => ({
        label: i18n.t('general.actions.unsetCoverPhoto'),
        prefix: <Icon value="image/photo" />,
        isDisabled: isProcessing,
        onClick: () => props.onUnsetCoverPhoto(photo),
      }))
      .when(props.canDeletePhotos, () => ({
        label: i18n.t('general.actions.delete'),
        prefix: <Icon value="action/delete" />,
        isDisabled: isProcessing,
        onClick: () => props.onDeletePhoto(photo),
      }));
  };

  return (
    <VStack spacing={4}>
      <HStack justify="space-between">
        <HStack spacing={2} align="center">
          <Heading size={4} alternative>
            {props.album.title}
          </Heading>
          <Heading size={4} alternative>
            {DOT_CHARACTER}
          </Heading>
          <Text size="base">{formatDateTime('dateShort', parseDate(props.album.createdAt))}</Text>
        </HStack>
        <Button
          data-testid={suffixTestId('downloadAll', props)}
          variant="link"
          title={i18n.t('general.actions.downloadAll')}
          leftIcon="file/download"
          onClick={() => props.onDownload(props.album.id)}
          size="small"
        />
      </HStack>
      <VehicleAlbumImageGrid>
        {props.album.photos.map((photo, index) => (
          <VehicleAlbumImage
            key={photo.id}
            src={prepareVehicleAlbumImageUrl(photo.url)}
            alt={i18n.t('entity.photo.labels.photo')}
            isCoverPhoto={photo.isCoverPhoto}
            isLoading={props.processingPhotoIds.includes(photo.id)}
            onClick={() => onOpen(index)}
            onContextMenu={(event) =>
              openDropdown(
                event,
                <>
                  {buildPhotoContextMenu(photo).map((item) => (
                    <DropdownItem key={item.label} {...item} />
                  ))}
                </>,
                {id: 'service-case-photo-context-menu'}
              )
            }
            data-testid={suffixTestId(`photo-[${index}]`, props)}
          />
        ))}
      </VehicleAlbumImageGrid>
      <Show when={isNotEmpty(props.album.videos)}>
        <HStack wrap spacing={4}>
          {props.album.videos.map((video, index) => (
            // Video tiles continue the photo grid above, so they share its tile footprint.
            <Box
              key={video.id}
              width={VEHICLE_ALBUM_IMAGE_SIZE.width}
              height={VEHICLE_ALBUM_IMAGE_SIZE.height}
            >
              <VideoPreview
                videoAsset={video}
                isLoading={!isVideoPlayable(video)}
                onOpenPreview={() =>
                  onOpen(
                    getPrecedingPlayableVideos(props.album.videos, index) +
                      props.album.photos.length
                  )
                }
                data-testid={suffixTestId('video', props)}
              />
            </Box>
          ))}
        </HStack>
      </Show>
      <Lightbox
        data-testid={suffixTestId('lightbox', props)}
        controls={lightboxControls}
        images={makeLightboxPhotos(props.album.photos)}
        videos={makeLightboxVideos(props.album.videos)}
      />
      {photoDownloadSizeDialog}
    </VStack>
  );
}
